import {
  Box,
  Button,
  Fade,
  FormControl,
  FormLabel,
  Link,
  TextField,
} from '@mui/material';
import React, { ChangeEvent, Fragment, useRef, useState } from 'react';
import { Alert } from '@components/Alert';
import { useAuth } from '../../../context/AuthProvider';
import {
  atLeast,
  isNotEmpty,
  isValidEmailAddress,
} from '../../../helpers/formHelpers';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { WindowPageTitle } from '@components/WindowPageTitle';
import { PanelTitle } from '../components/PanelTitle';
import { ROUTES } from '@routes/routes';

export const Login: React.FC = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleLogin } = useAuth();
  const { t } = useTranslation();

  const passwordInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState(location.state?.username || '');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [loginFailed, setLoginFailed] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  const from = location.state?.from?.pathname || '/';

  const hideLoginError = (): void => {
    setLoginFailed(false);
  };

  const showLoginError = (): void => {
    setLoginFailed(true);
  };

  const clearFormErrors = (): void => {
    setEmailError(false);
    setEmailErrorMessage('');

    setPasswordError(false);
    setPasswordErrorMessage('');
  };

  const validateInputs = (): void => {
    clearFormErrors();

    if (!isNotEmpty(email)) {
      setEmailError(true);
      setEmailErrorMessage(t('errors.required'));
    } else if (!isValidEmailAddress(email)) {
      setEmailError(true);
      setEmailErrorMessage(t('errors.invalidEmail'));
    }

    if (!isNotEmpty(password)) {
      setPasswordError(true);
      setPasswordErrorMessage(t('errors.required'));
    } else if (!atLeast(password, 5)) {
      setPasswordError(true);
      setPasswordErrorMessage(
        t('errors.atLeast', {
          field: t('forms:login.labels.password').toLocaleLowerCase(),
          min: 5,
        })
      );
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    hideLoginError();
    setIsLoggingIn(true);

    if (emailError || passwordError) {
      setIsLoggingIn(false);

      return;
    }

    const result = await handleLogin(email, password);

    if (!result) {
      setPassword('');
      passwordInputRef.current?.focus();
      showLoginError();
      setIsLoggingIn(false);

      return;
    }

    navigate(from, { replace: true });
  };

  return (
    <Fragment>
      <WindowPageTitle title={t('forms:login.title')} />
      <PanelTitle title={t('forms:login.title')} />

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
        }}
      >
        <Fade in={loginFailed} appear unmountOnExit>
          <Alert
            severity="error"
            variant="filled"
            onClose={hideLoginError}
            content={t('forms:login.errors.invalidLoginAttempt')}
          />
        </Fade>

        <FormControl>
          <FormLabel htmlFor="email" error={emailError}>
            {t('forms:login.labels.email')}
          </FormLabel>
          <TextField
            error={emailError}
            helperText={emailErrorMessage}
            id="email"
            type="email"
            name="email"
            placeholder={t('forms:login.placeholders.email')}
            autoComplete="email"
            autoFocus
            required
            fullWidth
            value={email}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
            variant="outlined"
            color={emailError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="password" error={passwordError}>
            {t('forms:login.labels.password')}
          </FormLabel>
          <TextField
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            inputRef={passwordInputRef}
            placeholder={t('forms:login.placeholders.password')}
            type="password"
            id="password"
            autoComplete="current-password"
            required
            fullWidth
            value={password}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setPassword(event.target.value)
            }
            variant="outlined"
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>
        {/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            /> */}
        {/* <ForgotPassword open={open} handleClose={handleClose} /> */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          onClick={validateInputs}
          disabled={isLoggingIn}
        >
          {t('forms:login.buttons.signIn')}
        </Button>
        <Link
          component="button"
          type="button"
          onClick={() =>
            navigate(ROUTES.public.forgotPassword, {
              state: {
                username: email,
              },
            })
          }
          variant="body2"
          sx={{ alignSelf: 'center' }}
        >
          {t('forms:login.buttons.forgotPassword')}
        </Link>
      </Box>
    </Fragment>
  );
};
